import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import GoogleMapWithDirections from 'src/@carketa/sales-packet/components/GoogleMapWithDirections';
import { useJsApiLoader, type Libraries } from '@react-google-maps/api';
import { CarketaDisclosure } from 'src/@carketa/sales-packet/components/CarketaDisclosure';
import { useState } from 'react';
import { useAnalytics } from 'use-analytics';
import { GOOGLE_MAPS_API_KEY } from 'src/utils/google/maps/clientKey';
import { Box } from 'src/ui/box';
import { Button } from 'src/ui/button';
import { Input } from 'src/ui/input';
import { Separator } from 'src/ui/separator';
import { Skeleton } from 'src/ui/skeleton';
import { Typography } from 'src/ui/typography';
import { cn } from 'src/utils/@/lib/utils';

const GOOGLE_MAPS_LIBRARIES: Libraries = ['geometry'];

const contactLinkClassName =
  'cursor-pointer text-xs text-primary no-underline hover:bg-white/25';

const schema = z.object({
  fromZip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'ZIP code must be 5 digits'),
  toZip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'ZIP code must be 5 digits'),
});

interface ShippingQuoteFields {
  fromZip: string;
  toZip: string;
  units: number;
  inop: boolean;
  is_enclosed: boolean;
}

type ShippingQuoteProps = {
  rate?: number | undefined;
  miles?: number | undefined;
  eta_min?: number | undefined;
  eta_max?: number | undefined;
  inop_fee?: number | undefined;
  rebate_rate?: number | undefined;
  error?: string | undefined;
  units: ShippingQuoteFields['units'];
};

interface TransportationProps {
  pdfMode?: boolean;
  vin: string;
}

const Transportation = ({ pdfMode, vin }: TransportationProps) => {
  const { track } = useAnalytics();

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ShippingQuoteFields>({
    defaultValues: {
      fromZip: '',
      toZip: '',
    },
    resolver: zodResolver(schema),
  });

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY, //this needs to be in the .env should call process.env.GOOGLE_MAP_API_KEY
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [quote, setQuote] = useState<ShippingQuoteProps | null>(null);

  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');

  const rate = quote?.rate || 0;
  const perVehicleRate = (rate + (quote?.inop_fee || 0)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const perVehicleRateFormatted = perVehicleRate.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const totalRate = ((rate || 0) + (quote?.inop_fee || 0)) * 1;

  const totalRateFormatted = totalRate.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  const getQuote = async (data: ShippingQuoteFields) => {
    setIsLoading(true);

    const url = new URL('/api/shipping/quote', window.location.origin);
    url.searchParams.append('pickup', data.fromZip);
    url.searchParams.append('destination', data.toZip);
    url.searchParams.append('units', '1');
    url.searchParams.append('inop', '0');
    url.searchParams.append('is_enclosed', '0');
    url.searchParams.append('vin', vin);

    track(
      'transportation-get-quote-submit',
      {
        url: url.toString(),
      },
      {
        component: 'sales-packet',
      }
    );

    const response = await fetch(url.toString());

    const results = await response.json();

    track(
      'transportation-get-quote-results',
      {
        results,
      },
      {
        component: 'sales-packet',
      }
    );

    if (response.ok) {
      // Pass ZIPs to the map; GoogleMapWithDirections qualifies them for
      // DirectionsService (e.g. "84065, USA" + region: "us"). Do not call
      // zipCodeToPlaceName — it uses server-only GOOGLE_MAPS_BACKEND_API_KEY.
      setQuote(results);
      setOrigin(String(results.pickup ?? data.fromZip));
      setDestination(String(results.destination ?? data.toZip));
      setIsLoading(false);
      return results;
    }

    setIsLoading(false);
    console.error('An error occurred while fetching the shipping quote');
    return null;
  };

  return (
    <>
      <Typography variant="h6" className="mb-10 mt-10 text-black">
        Transportation
      </Typography>
      <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded">
        <div className="p-3">
          <Typography variant="h6" className="text-black">
            Get a quote
          </Typography>
          <Typography variant="caption">Enter shipping information</Typography>
          <form
            onSubmit={handleSubmit((data) => {
              getQuote(data);
            })}
          >
            <Box className="mb-12 mt-8 flex items-center gap-8">
              <div className="w-full">
                <Controller
                  name="fromZip"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      value={value}
                      label="Origin Zip Code"
                      onChange={onChange}
                      className={cn(
                        'h-14 w-full',
                        errors.fromZip && 'border-destructive'
                      )}
                    />
                  )}
                />
                {errors?.fromZip && (
                  <p className="mt-1 text-sm text-destructive">
                    {errors.fromZip.message || ''}
                  </p>
                )}
              </div>
              <div className="w-full">
                <Controller
                  name="toZip"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      value={value}
                      label="Destination Zip Code"
                      onChange={onChange}
                      className={cn(
                        'h-14 w-full',
                        errors.toZip && 'border-destructive'
                      )}
                    />
                  )}
                />
                {errors?.toZip && (
                  <p className="mt-1 text-sm text-destructive">
                    {errors.toZip.message || ''}
                  </p>
                )}
              </div>
            </Box>

            <Box className="mb-12 flex items-center">
              <Button size="lg" type="submit" variant="contained" className="mr-6">
                Get Quote
              </Button>
              <Button
                size="lg"
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setQuote(null);
                  reset();
                }}
              >
                Clear
              </Button>
            </Box>
          </form>
          <Separator className="mb-12" />
          <Typography variant="caption">
            Having Transportation Issues?{' '}
            <a
              className={contactLinkClassName}
              href={`mailto:carketach@acertusdelivers.com`}
            >
              carketach@acertusdelivers.com
            </a>{' '}
            <a className={contactLinkClassName} href={`tel:(855) 431-6570`}>
              (855) 431-6570
            </a>
          </Typography>
        </div>
      </div>
      {isLoading ? (
        <Box className="mt-10">
          <Skeleton variant="rectangle" width="100%" height={100} />
        </Box>
      ) : (
        quote && (
          <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded mt-4">
            <div className="p-3">
              <Typography variant="h6" className="text-black">
                Quote Details
              </Typography>
              <div className="flex mt-6">
                <strong className="text-12 mb-1 mr-2">Rate:</strong>
                <Typography variant="caption" className="ml-2">
                  {quote?.rate
                    ? `$${perVehicleRateFormatted}

          per unit = $${totalRateFormatted} total`
                    : ''}
                </Typography>
              </div>
              <div className="flex">
                <strong className="text-12 mb-1 mr-2">Miles:</strong>
                <Typography variant="caption" className="ml-2">
                  {quote?.miles?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </Typography>
              </div>
              <div className="flex">
                <strong className="text-12 mb-1 mr-2">ETA:</strong>
                <Typography variant="caption" className="ml-2">
                  {quote?.eta_min && quote.eta_max ? `${quote?.eta_min} - ${quote?.eta_max} days` : ''}
                </Typography>
              </div>

              <div className="w-full mt-2">
                {loadError ? null : !isLoaded ? (
                  <p>Loading...</p>
                ) : (
                  <GoogleMapWithDirections
                    origin={origin}
                    destination={destination}
                  />
                )}
              </div>
            </div>
          </div>
        )
      )}
      {pdfMode && <CarketaDisclosure />}
    </>
  );
};

export default Transportation;
